import { all, call, put, takeLatest, take, retry, select, race } from 'redux-saga/effects';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import * as apollo from 'apollo/notifications.query';
import * as reminders from 'apollo/reminders.query';
import * as accounts from 'apollo/accounts.query';
import { pushSiteError } from 'containers/App/actions';
import { updateLoginPreferences } from 'api/preferences';
import { fetchUserInfo, fetchLoginWeddings } from './query.gql';
import * as constants from './constants';
import { UPDATE_EVENT, REMOVE_EVENT } from '../Events/types';

dayjs.extend(utc);

function* getCheckIn(action) {
  const { tries } = action || 0;
  try {
    if (tries > 5) {
      // #TODO: Add failure modal to notify user that checkin has failed.
      return;
    }
    const results = yield call(apollo.fetchCheckIn);
    const response = results?.data?.notificationInbox || {};
    yield put ({ type: constants.GET_CHECKIN_SUCCESS, response });
  } catch (err) {
    yield put ({ type: constants.GET_CHECKIN, tries: tries + 1 });
  }
}

function* getPaginatedNotifications(action) {
  try {
    const result = yield call(apollo.fetchNotifications, action.payload);
    const response = result?.data?.notificationInbox || {};
    yield put({ type: constants.GET_NOTIFICATIONS_SUCCESS, payload: response });
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* getAddtNotifications(action) {
  try {
    const result = yield call(apollo.fetchNotifications, action.payload);
    const response = result?.data?.notificationInbox || {};
    yield put({ type: constants.GET_ADDT_NOTIFICATIONS_SUCCESS, payload: response });
  } catch (err) {
    // yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* markAllNotificationsFunction(action) {
  try {
    const mutateResult = yield call(apollo.readAllNotifications, action.payload);
    const mutateResponse = mutateResult?.data?.markAllNotificationsAsRead || {};
    if (mutateResponse) {
      const result = yield call(apollo.fetchNotifications, action.payload);
      const response = result?.data?.notificationInbox || {};
      yield put({ type: constants.GET_NOTIFICATIONS_SUCCESS, payload: response });
    }
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* markNotificationFunction(action) {
  try {
    const mutateResult = yield call(apollo.readNotification, action.payload);
    const mutateResponse = mutateResult?.data?.markNotificationAsRead || {};
    if (mutateResponse) {
      const result = yield call(apollo.fetchNotifications, action.payload);
      const response = result?.data?.notificationInbox || {};
      yield put({ type: constants.GET_NOTIFICATIONS_SUCCESS, payload: response });
    }
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* getTasks() {
  try {
    const result = yield call(apollo.fetchTasks);
    const response = result?.data?.notificationInbox || {};
    yield put({ type: constants.GET_TASKS_SUCCESS, payload: response });
  } catch (err) {
    yield put ({ type: constants.GET_TASKS_FAIL });
  }
}

function* getSingleTask(action) {
  try {
    const { id, module } = action.payload;
    const data = yield call(apollo.fetchTask, { id });
    if (data.task) {
      yield put({
        type: constants.FETCH_SINGLE_TASK_SUCCESS,
        payload: data.task,
        module,
        taskId: id,
      });
    }
  } catch (err) {
    yield put({ type: constants.FETCH_SINGLE_TASK_FAIL });
  }
};

function* getGeneralTasks() {
  try {
    const result = yield call(apollo.fetchGeneralTasks);
    const response = result?.data?.notificationInbox || {};
    yield put({ type: constants.GET_TASKS_SUCCESS, payload: response });
  } catch (err) {
    yield put ({ type: constants.GET_TASKS_FAIL });
  }
}

function* getProjectTasks() {
  try {
    const result = yield call(apollo.fetchProjectTasks);
    const response = result?.data?.notificationInbox || {};
    yield put({ type: constants.GET_TASKS_SUCCESS, payload: response });
  } catch (err) {
    yield put ({ type: constants.GET_TASKS_FAIL });
  }
}

function* getLeadTasks() {
  try {
    const result = yield call(apollo.fetchLeadTasks);
    const response = result?.data?.notificationInbox || {};
    yield put({ type: constants.GET_TASKS_SUCCESS, payload: response });
  } catch (err) {
    yield put ({ type: constants.GET_TASKS_FAIL });
  }
}

function* getFocusTasks() {
  try {
    const result = yield call(apollo.fetchFocusTasks);
    const response = result?.data?.notificationInbox || {};
    yield put({ type: constants.GET_FOCUS_TASKS_SUCCESS, payload: response.tasksInfo.assignedTasks });
  } catch (err) {
    yield put ({ type: constants.GET_TASKS_FAIL });
  }
}

function* addNewAssignment(action) {
  try {
    const mutateResult = yield call(apollo.addTaskAssignment, action.payload);
    const mutateResponse = mutateResult?.data?.addTaskAssignment;
    yield put ({
      type: constants.UPDATE_TASK_SUCCESS,
      payload: mutateResponse,
      taskId: action.payload.taskId,
      dataType: 'assignments',
      primaryKey: 'loginId',
      operation: 'create',
    });
    // Adds to the event assignment list if it's available.
    const events = yield select(state => state.events.events);
    const taskEvent = events[action.payload.taskId] || null;
    if (taskEvent) {
      yield put ({
        type: UPDATE_EVENT,
        payload: {
          id: action.payload.taskId,
          data: {
            assigned: [...taskEvent.assigned, mutateResponse],
          },
        },
      });
    }
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* updateAssignment(action) {
  try {
    const mutateResult = yield call(apollo.updateTaskAssignment, action.payload);
    const mutateResponse = mutateResult?.data?.updateTaskAssignment;
    yield put ({
      type: constants.UPDATE_TASK_SUCCESS,
      payload: mutateResponse,
      taskId: action.payload.taskId,
      dataType: 'assignments',
      primaryKey: 'loginId',
      operation: 'update',
    });
    /**
     * #TODO: This is pretty naive, so could be improved by directly altering the
     * state instead of refetching, but need to finish order by index first.
     */
    // Need to update myFocus list completely if updating assignment.
    yield put ({ type: constants.GET_FOCUS_TASKS });
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* dropAssignment(action) {
  try {
    const mutateResult = yield call(apollo.dropTaskAssignment, action.payload);
    const mutateResponse = mutateResult?.data?.dropTaskAssignment;
    yield put ({
      type: constants.UPDATE_TASK_SUCCESS,
      payload: { ...mutateResponse, loginId: action.payload.loginId },
      taskId: action.payload.taskId,
      dataType: 'assignments',
      primaryKey: 'loginId',
      operation: 'delete',
    });
    // Update event assignment list if it's available.
    const events = yield select(state => state.events.events);
    const taskEvent = events[action.payload.taskId] || null;
    if (taskEvent) {
      yield put ({
        type: UPDATE_EVENT,
        payload: {
          id: action.payload.taskId,
          data: {
            assigned: taskEvent.assigned.filter(te => te.loginId !== action.payload.loginId),
          },
        },
      });
    }
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* createTaskComment(action) {
  try {
    const mutateResult = yield call(apollo.createTaskComment, action.payload);
    const mutateResponse = mutateResult?.data?.createTaskComment;
    yield put ({
      type: constants.UPDATE_TASK_SUCCESS,
      payload: mutateResponse,
      taskId: action.payload.taskId,
      primaryKey: 'id',
      dataType: 'comments',
      operation: 'create',
    });
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* updateTaskComment(action) {
  try {
    const mutateResult = yield call(apollo.updateTaskComment, action.payload);
    const mutateResponse = mutateResult?.data?.updateTaskComment;
    yield put ({
      type: constants.UPDATE_TASK_SUCCESS,
      payload: mutateResponse,
      taskId: action.payload.taskId,
      primaryKey: 'id',
      dataType: 'comments',
      operation: 'update',
    });
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* deleteTaskComment(action) {
  try {
    const mutateResult = yield call(apollo.deleteTaskComment, action.payload);
    const mutateResponse = mutateResult?.data?.deleteTaskComment;
    yield put ({
      type: constants.UPDATE_TASK_SUCCESS,
      payload: { ...mutateResponse, id: action.payload.commentId },
      taskId: action.payload.taskId,
      primaryKey: 'id',
      dataType: 'comments',
      operation: 'delete',
    });
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* createNotificationTask(action) {
  const { module, ...rest } = action.payload;
  try {
    let mutateResult;
    if (module === 'loginTasks' || module === 'assignedTasks') {
      mutateResult = yield call(apollo.createLoginTask, rest);
    }
    if (module === 'weddings') {
      mutateResult = yield call(apollo.createWeddingTask, rest);
    }
    if (module === 'leads') {
      mutateResult = yield call(apollo.createLeadTask, rest);
    }
    if (mutateResult?.data) {
      let response;
      if (module === 'loginTasks' || module === 'assignedTasks') {
        const result = yield call(apollo.fetchTasks, action.payload);
        response = result?.data?.notificationInbox || {};
      }

      if (module === 'weddings') {
        const result = yield call(getProjectTasks, action.payload);
        response = result?.data?.notificationInbox || {};
      }
      if (module === 'leads') {
        const result = yield call(getLeadTasks, action.payload);
        response = result?.data?.notificationInbox || {};
      }

      yield put({ type: constants.GET_NOTIFICATIONS_SUCCESS, payload: response });
    }
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* updateNotificationTask(action) {
  const { module, ...rest } = action.payload;
  try {
    let mutateResult;
    if (module === 'loginTasks' || module === 'assignedTasks') {
      const response = yield call(apollo.updateLoginTask, rest);
      mutateResult = response.data.updateLoginTask;
    }
    if (module === 'weddings') {
      const response = yield call(apollo.updateWeddingTask, rest);
      mutateResult = response.data.updateWeddingTask;
    }
    if (module === 'leads') {
      const response = yield call(apollo.updateLeadTask, rest);
      mutateResult = response.data.updateLeadTask;
    }

    yield put({
      type: constants.UPDATE_TASK_DETAIL_SUCCESS,
      payload: mutateResult,
      module,
      weddingId: module === 'weddings' ? rest.weddingId : undefined,
      leadId: module === 'leads' ? rest.leadId : undefined,
    });
    const start = dayjs.utc(mutateResult.dueDate).toISOString();
    yield put({
      type: UPDATE_EVENT,
      payload: {
        id: mutateResult.id,
        data: {
          ...mutateResult,
          start,
          end: start,
          title: mutateResult.name || '',
          allDay: true,
          type: 'checklist',
          taskId: mutateResult.id,
          transparent: true,
          assigned: mutateResult.assignments || [],
          taskType: module,
          clientVisible: mutateResult.weddingItem?.clientVisible,
          weddingId: rest.weddingId,
          leadId: rest.leadId,
        },
      },
    });
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* deleteNotificationTask(action) {
  const { module, ...rest } = action.payload;
  try {
    let mutateResult;
    if (module === 'loginTasks' || module === 'assignedTasks') {
      const response = yield call(apollo.deleteLoginTask, rest);
      mutateResult = response.data.deleteLoginTask;
    }
    if (module === 'weddings') {
      const response = yield call(apollo.deleteWeddingTask, rest);
      mutateResult = response.data.deleteWeddingTask;
    }
    if (module === 'leads') {
      const response = yield call(apollo.deleteLeadTask, rest);
      mutateResult = response.data.deleteLeadTask;
    }

    yield put({
      type: constants.DELETE_TASK_SUCCESS,
      module,
      taskId: rest.taskId ?? rest.id,
      payload: mutateResult,
      weddingId: module === 'weddings' ? rest.weddingId : undefined,
      leadId: module === 'leads' ? rest.leadId : undefined,
    });
    yield put({
      type: REMOVE_EVENT,
      payload: {
        id: rest.taskId ?? rest.id,
      },
    });
  } catch (err) {
    yield put({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* createTaskReminder(action) {
  try {
    const result = yield call(reminders.createTaskReminder, action.payload);
    const response = result.data?.createTaskReminder;
    yield put({
      type: constants.TASK_REMINDER_SUCCESS,
      payload: { ...response, created: true },
      module: action.module,
      scope: action.scope,
      isMyFocus: action.isMyFocus,
    });
  } catch (err) {
    yield put ({ type: constants.TASK_REMINDER_FAILURE });
  }
}

function* updateTaskReminder(action) {
  try {
    const result = yield call(reminders.updateTaskReminder, action.payload);
    const response = result.data?.updateTaskReminder;
    yield put({
      type: constants.TASK_REMINDER_SUCCESS,
      payload: response,
      module: action.module,
      scope: action.scope,
      isMyFocus: action.isMyFocus,
    });
  } catch (err) {
    yield put ({ type: constants.TASK_REMINDER_FAILURE });
  }
}

function* deleteTaskReminder(action) {
  try {
    const result = yield call(reminders.deleteTaskReminder, action.payload);
    const response = result.data?.deleteTaskReminder?.deleted;
    const payload = {
      taskId: action.payload.taskId,
      definitionId: action.payload.definitionId,
      deleted: response,
    };
    yield put({
      type: constants.TASK_REMINDER_SUCCESS,
      payload,
      module: action.module,
      scope: action.scope,
      isMyFocus: action.isMyFocus,
    });
  } catch (err) {
    yield put ({ type: constants.TASK_REMINDER_FAILURE });
  }
}

function* reOrderTask(action) {
  try {
    const { module } = action.params || {};
    let mutateResult;
    if (module === 'assignedTasks') {
      mutateResult = yield call(apollo.orderBeforeFocusedTask, action.payload);
    } else {
      mutateResult = yield call(apollo.orderBeforeAssignedTask, action.payload);
    }

    if (mutateResult) {
      let result;
      switch (module) {
        case 'assignedTasks':
          result = yield call(apollo.fetchFocusTasks, action.payload);
          break;
        case 'loginTasks':
          result = yield call(apollo.fetchGeneralTasks, action.payload);
          break;
        case 'weddings':
          result = yield call(apollo.fetchProjectTasks, action.payload);
          break;
        case 'leads':
          result = yield call(apollo.fetchLeadTasks, action.payload);
          break;
        default:
          break;
      }
      if (result) {
        const response = result?.data?.notificationInbox || {};
        yield put({ type: constants.GET_TASKS_SUCCESS, payload: response });
      } else {
        yield put ({ type: constants.GET_NOTIFICATIONS_FAIL });
      }
    }
  } catch (err) {
    yield put ({ type: constants.GET_NOTIFICATIONS_FAIL });
  }
}

function* updateUserInfo(action) {
  const { username, email } = action.payload;
  let isUsernameExisted = false;
  let isEmailExisted = false;
  try {
    if (username) {
      isUsernameExisted = yield call(accounts.checkUsername, username);
    }

    if (email) {
      isEmailExisted = yield call(accounts.checkEmail, email);
    }

    if (isUsernameExisted || isEmailExisted) {
      yield put({
        type: constants.UPDATE_USER_INFO_FAIL,
        payload: {
          field: isUsernameExisted ? 'username' : 'email',
          error: `'${
            isUsernameExisted ? username : email
          }' is already a registered ${
            isUsernameExisted ? 'username' : 'email address'
          }`,
        },
      });
      return;
    }

    const response = yield call(accounts.updateUserInfo, action.payload);
    if (!response.errors) {
      yield put({
        type: constants.UPDATE_USER_INFO_SUCCESS,
        payload: response.data.userProfile.login,
      });
    } else {
      yield put({ type: constants.UPDATE_USER_INFO_FAIL });
    }
  } catch (err) {
    yield put({ type: constants.UPDATE_USER_INFO_FAIL });
  }
}

function* getUserInfo() {
  const MAX_RETRIES = 5;
  const RETRY_DELAY = 3000;
  try {
    const { response, cancel } = yield race({
      response: all([
        retry(MAX_RETRIES, RETRY_DELAY, fetchUserInfo),
        retry(MAX_RETRIES, RETRY_DELAY, fetchLoginWeddings, { pageSize: 100, page: 0 }),
      ]),
      cancel: take(constants.PUSH_AUTH_ERROR),
    });
    if (cancel) return;
    const [userInfo, loginWeddings] = response || [];

    if (!userInfo.errors && !loginWeddings.errors) {
      const payload = { ...userInfo.data.me, loginWeddings: loginWeddings.data.loginWeddings };
      yield put({ type: constants.GET_USER_INFO_SUCCESS });
      yield put({ type: constants.SET_USER_INFO, payload });
    } else {
      const errors = userInfo?.errors ? userInfo.errors : loginWeddings?.errors;
      yield put({ type: constants.GET_USER_INFO_FAIL, payload: errors });
    }
  } catch (err) {
    yield put({ type: constants.GET_USER_INFO_FAIL, payload: err });
  }
}

function* getNotificationPreferences() {
  try {
    const response = yield call(accounts.getNotificationPreferences);
    yield put({ type: constants.GET_NOTIFICATION_PREFERENCES_SUCCESS, payload: response });

  } catch (error) {
    yield put({ type: constants.GET_NOTIFICATION_PREFERENCES_FAIL });
  }
}

function* saveNotificationPreferences(action) {
  try {
    const response = yield call(accounts.saveNotificationPreferences, action.payload);
    yield put({ type: constants.SAVE_NOTIFICATION_PREFERENCE_SUCCESS, payload: response });

  } catch (error) {
    yield put({ type: constants.SAVE_NOTIFICATION_PREFERENCE_FAIL });
  }
}

function* updateLoginSavedColors(action) {
  const { payload } = action || {};

  try {
    const response = yield call(accounts.updateLoginSavedColors, payload);
    yield put({ type: constants.UPDATE_LOGIN_SAVED_COLORS_SUCCESS, payload: response });
  } catch (error) {
    yield put({ type: constants.UPDATE_LOGIN_SAVED_COLORS_FAIL });
  }
};

function* createIntegrationVCalGrant(action) {
  const { payload } = action || {};
  try {
    const response = yield call(accounts.createIntegrationVCalGrant, payload);
    if (!response.errors && response.data.createIntegrationVcalGrant) {
      yield put({
        type: constants.CREATE_INTEGRATION_VCAL_GRANT_SUCCESS,
        payload: response.data.createIntegrationVcalGrant,
      });
    } else {
      yield put({ type: constants.CREATE_INTEGRATION_VCAL_GRANT_FAIL });
    }
  } catch (error) {
    yield put({ type: constants.CREATE_INTEGRATION_VCAL_GRANT_FAIL });
  }
};

function* deleteIntegrationAccount(action) {
  const { payload } = action || {};
  try {
    yield put({ type: constants.DELETE_INTEGRATION_ACCOUNT_INIT, payload: { id: payload } });
    const response = yield call(accounts.deleteIntegrationAccount, payload);
    if (!response.errors && response.data.deleteIntegrationAccount.isDeleted) {
      yield put({ type: constants.DELETE_INTEGRATION_ACCOUNT_SUCCESS, payload: { id: payload } });
      yield call(updateLoginPreferences, { 'calendar.externalCalendarsDisplay': {} });
    } else {
      yield put({ type: constants.DELETE_INTEGRATION_ACCOUNT_FAIL, payload: { id: payload } });
    }
  } catch (error) {
    yield put({ type: constants.DELETE_INTEGRATION_ACCOUNT_FAIL, payload: { id: payload } });
  }
};

function* createLoginCalendar(action) {
  const { payload } = action || {};
  try {
    yield put({ type: constants.CREATE_LOGIN_CALENDAR_INIT, payload });
    const response = yield call(accounts.createLoginCalendar, payload);
    if (!response.errors && response.data.createLoginCalendar.loginCalendar) {
      yield put({ type: constants.CREATE_LOGIN_CALENDAR_SUCCESS, payload: response.data.createLoginCalendar });
    } else {
      if (response.errors[0]) yield put(pushSiteError(response.errors[0]));
      yield put({ type: constants.CREATE_LOGIN_CALENDAR_FAIL, payload });
    }
  } catch (error) {
    yield put(pushSiteError({
      message: 'There was a problem creating your calendar. Please try again in a moment.',
    }));
    yield put({ type: constants.CREATE_LOGIN_CALENDAR_FAIL, payload });
  }
};

function* deleteLoginCalendar(action) {
  const { payload } = action || {};
  try {
    yield put({ type: constants.DELETE_LOGIN_CALENDAR_INIT, payload });
    const response = yield call(accounts.deleteLoginCalendar, payload);
    if (!response.errors && response.data.deleteLoginCalendar.isDeleted) {
      yield put({ type: constants.DELETE_LOGIN_CALENDAR_SUCCESS, payload: response.data.deleteLoginCalendar });
    } else {
      if (response.errors[0]) yield put(pushSiteError(response.errors[0]));
      yield put({ type: constants.DELETE_LOGIN_CALENDAR_FAIL, payload });
    }
  } catch (error) {
    yield put(pushSiteError({
      message: 'There was a problem deleting your calendar. Please try again in a moment.',
    }));
    yield put({ type: constants.DELETE_LOGIN_CALENDAR_FAIL, payload });
  }
};

export default function* watchList() {
  yield takeLatest(constants.GET_CHECKIN, getCheckIn);
  yield takeLatest(constants.GET_NOTIFICATIONS, getPaginatedNotifications);
  yield takeLatest(constants.GET_ADDT_NOTIFICATIONS, getAddtNotifications);
  yield takeLatest(constants.MARK_NOTIFICATIONS_READ, markAllNotificationsFunction);
  yield takeLatest(constants.MARK_NOTIFICATION_READ, markNotificationFunction);

  yield takeLatest(constants.CREATE_NEW_COMMENT, createTaskComment);
  yield takeLatest(constants.UPDATE_COMMENT_DETAIL, updateTaskComment);
  yield takeLatest(constants.DELETE_COMMENT, deleteTaskComment);
  yield takeLatest(constants.ADD_NEW_ASSIGNMENT, addNewAssignment);
  yield takeLatest(constants.UPDATE_ASSIGNMENT, updateAssignment);
  yield takeLatest(constants.DROP_ASSIGNMENT, dropAssignment);

  yield takeLatest(constants.GET_TASKS, getTasks);
  yield takeLatest(constants.GET_GENERAL_TASKS, getGeneralTasks);
  yield takeLatest(constants.GET_PROJECT_TASKS, getProjectTasks);
  yield takeLatest(constants.GET_LEAD_TASKS, getLeadTasks);
  yield takeLatest(constants.GET_FOCUS_TASKS, getFocusTasks);
  yield takeLatest(constants.FETCH_SINGLE_TASK, getSingleTask);
  yield takeLatest(constants.CREATE_NEW_TASK, createNotificationTask);
  yield takeLatest(constants.UPDATE_TASK_DETAIL, updateNotificationTask);
  yield takeLatest(constants.DELETE_TASK, deleteNotificationTask);

  yield takeLatest(constants.CREATE_TASK_REMINDER, createTaskReminder);
  yield takeLatest(constants.UPDATE_TASK_REMINDER, updateTaskReminder);
  yield takeLatest(constants.DELETE_TASK_REMINDER, deleteTaskReminder);

  yield takeLatest(constants.REORDER_TASK, reOrderTask);

  yield takeLatest(constants.UPDATE_USER_INFO, updateUserInfo);
  yield takeLatest(constants.GET_USER_INFO, getUserInfo);

  yield takeLatest(constants.GET_NOTIFICATION_PREFERENCES, getNotificationPreferences);
  yield takeLatest(constants.SAVE_NOTIFICATION_PREFERENCE, saveNotificationPreferences);

  yield takeLatest(constants.UPDATE_LOGIN_SAVED_COLORS, updateLoginSavedColors);

  yield takeLatest(constants.CREATE_INTEGRATION_VCAL_GRANT, createIntegrationVCalGrant);
  yield takeLatest(constants.DELETE_INTEGRATION_ACCOUNT, deleteIntegrationAccount);
  yield takeLatest(constants.CREATE_LOGIN_CALENDAR, createLoginCalendar);
  yield takeLatest(constants.DELETE_LOGIN_CALENDAR, deleteLoginCalendar);
}
